/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Observable, Subject, map } from "rxjs";
import {
    ButtonComponent, DatetimePipe, DeleteResourceSectionComponent, IntegrationCardComponent,
    PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent
} from "typedb-cloud-framework";
import { mergeChanges } from "../../../../concept/api-response";
import { AWSAccount, AzureAccount, CreditCard, GCPAccount, Invoice } from "../../../../concept/subscription";
import { BillingAddressPipe } from "../../billing-address.pipe";
import { CreditCardExpiryDatePipe } from "../../credit-card-expiry-date.pipe";
import { CreditCardTypePipe } from "../../credit-card-type.pipe";
import { CurrencyPipe } from "../../currency.pipe";
import { PaymentDetailsModalComponent } from "../../payment-details-modal/payment-details-modal.component";
import { DialogResult } from "../../../../service/dialog.service";

import { SubscriptionApi } from "../../../../service/subscription/subscription-api.service";
import { ApplicationState } from "../../../../service/application-state.service";
import { SnackbarService } from "../../../../service/snackbar.service";
import { SubscriptionController } from "../../../../service/subscription/subscription-controller.service";
import { InvoicesTableComponent } from "../../invoices-table/invoices-table.component";
import { TablePaginatorComponent } from "../../../../framework/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
    MarketplaceCardsComponent
} from "../../marketplace-cards/marketplace-cards.component";
import { CreditsTableComponent } from "../../credits-table/credits-table.component";
import { DiscountsTableComponent } from "../../discounts-table/discounts-table.component";

@Component({
    selector: "tp-team-settings-billing",
    templateUrl: "./team-settings-billing.component.html",
    styleUrls: ["./team-settings-billing.component.scss", "../settings.scss"],
    imports: [
        PropertiesTableComponent, AsyncPipe, ButtonComponent, SpinnerComponent,
        InvoicesTableComponent, TablePaginatorComponent, PropertiesTableRowComponent,
        CreditCardTypePipe, CreditCardExpiryDatePipe, BillingAddressPipe, DatetimePipe, CurrencyPipe,
        MatTooltipModule, IntegrationCardComponent,
        MarketplaceCardsComponent, CreditsTableComponent, DiscountsTableComponent,
    ]
})
export class TeamSettingsBillingComponent implements OnInit, OnDestroy {
    readonly team = this.app.requireCurrentTeam();

    readonly upcomingInvoice$ = new BehaviorSubject<Invoice | "none" | null>(null);
    readonly creditCard$ = new BehaviorSubject<CreditCard | null>(null);
    readonly activeGcpAccount$ = new BehaviorSubject<GCPAccount | null>(null);
    readonly azureAccount$ = new BehaviorSubject<AzureAccount | null>(null);
    readonly activeAwsAccount$ = new BehaviorSubject<AWSAccount | null>(null);
    private readonly upcomingInvoiceUnsub$ = new Subject<void>();

    private readonly unsubInvoices$ = new Subject<void>();
    readonly invoices = this.subscriptionCtl.invoicesTable(this.unsubInvoices$);
    private readonly unsubCredits$ = new Subject<void>();
    readonly credits = this.subscriptionCtl.creditsTable(this.unsubCredits$);
    private readonly unsubDiscounts$ = new Subject<void>();
    readonly discounts = this.subscriptionCtl.discountsTable(this.unsubDiscounts$);

    cardSectionReady = false;

    constructor(
        private app: ApplicationState, private snackbar: SnackbarService, private dialog: MatDialog,
        private subscriptionApi: SubscriptionApi, private subscriptionCtl: SubscriptionController,
    ) {}

    ngOnInit() {
        this.initUpcomingInvoiceLoader();
        this.fetchCreditCards();
        this.fetchMarketplaceAccounts();
    }

    ngOnDestroy() {
        this.upcomingInvoiceUnsub$.next();
        this.unsubInvoices$.next();
        this.unsubCredits$.next();
    }

    initUpcomingInvoiceLoader() {
        const team = this.app.requireCurrentTeam();
        this.subscriptionApi.getUpcomingInvoice(team.uuid, this.upcomingInvoiceUnsub$).subscribe((res) => {
            if (!res) {
                this.upcomingInvoice$.next("none");
                return;
            }
            const current = this.upcomingInvoice$.value === "none" ? null : this.upcomingInvoice$.value;
            const mergeResult = mergeChanges(current, res);
            if (mergeResult.result === "shouldDelete") {
                this.upcomingInvoiceUnsub$.next();
                // TODO: ???
            } else if (mergeResult.result === "shouldResync") {
                console.warn(`Received update is not valid in the current state; forcing a full resync`);
                this.upcomingInvoiceUnsub$.next();
                this.initUpcomingInvoiceLoader();
                return;
            }
            if (mergeResult.data) {
                this.upcomingInvoice$.next(mergeResult.data);
            }
        });
    }

    openPaymentModal() {
        this.dialog.open<PaymentDetailsModalComponent, any, DialogResult>(PaymentDetailsModalComponent).afterClosed().subscribe(result => {
            if (result === "ok") {
                this.fetchCreditCards();
                this.snackbar.success("Payment method added successfully");
            }
            else if (result == null || result === "cancelled") return;
            else this.snackbar.errorPersistent(`Error setting up payment method: ${result.error}`);
        });
    }

    private fetchCreditCards() {
        const team = this.app.requireCurrentTeam();
        this.subscriptionApi.listCreditCards(team.uuid).subscribe((cards) => {
            this.creditCard$.next(cards[0]);
            this.cardSectionReady = true
        });
    }

    private fetchMarketplaceAccounts() {
        const team = this.app.requireCurrentTeam();
        this.subscriptionApi.getMarketplaceAccounts(team.uuid).subscribe((accounts) => {
            if (accounts.gcp?.status == "active") {
                this.activeGcpAccount$.next(accounts.gcp);
            }
            this.azureAccount$.next(accounts.azure);
            if (accounts.aws?.status == "active") {
                this.activeAwsAccount$.next(accounts.aws);
            }
        });
    }
}
